import React, { useContext, useEffect, useState } from "react";
import { t } from "i18next";
import { AuthContext } from "@/App.jsx";
import useFormState from "Utils/useFormState";
import CustomerSelect from "Components/customerSelect/CustomerSelect";
import Input from "Components/input/Input";
import RadioButton from "Components/radiobutton/RadioButton";
import Button from "Components/button/Button";
import WarningBanner from "Components/warningBanner/WarningBanner";
import QboxmailApi from "../../../qboxmailApi";
import ResourcesSelector from "Components/resourcesSelector/ResourcesSelector";
import { alertSuccess } from "Utils/alerts";
import { getAccountsFormData } from "Utils/suppressionListForm";
import { useHistory } from "react-router-dom";
import isEmail from "validator/lib/isEmail";
import s from "../delivery.scss";

const api = new QboxmailApi({ useCookie: true });

export const RestrictionRules = ({
  accounts,
  onSelectAllChange,
  checkedAccounts,
  onChange,
  showTitle = true,
  allSelected = false,
}) => {
  return (
    <>
      {showTitle && <div className="sectionTitle">{t("delivery.suppressionList.add.restrictionRules.title")}</div>}
      <ResourcesSelector
        resources={accounts.map((account) => ({
          value: account.id,
          label: `${account.username} - ${account.description}`,
          disabled: allSelected,
        }))}
        name="accounts"
        onChange={(value) => {
          onChange({ accounts: value.accounts });
        }}
        onSelectAllToggle={({ selectAll }) => onSelectAllChange(selectAll)}
        selectAllAsToggle
        selectAllLabel={t("delivery.suppressionList.add.restrictionRules.allAccountsToggle.label")}
        groupLabel={t("delivery.suppressionList.add.restrictionRules.singleAccountToggle.label")}
        checkeds={checkedAccounts}
        allSelected={allSelected}
      />
    </>
  );
};

const New = () => {
  const auth = useContext(AuthContext);
  const {
    formData,
    formErrors,
    setFormFieldValue,
    setFormLoading,
    isFormLoading,
    unsetFormLoading,
    handleFormResponseErrors,
    setFormErrors,
  } = useFormState({
    reason: "bounce",
  });
  const [accounts, setAccounts] = useState([]);
  const [blockAllAccounts, setBlockAllAccounts] = useState(true);
  const history = useHistory();

  const validateForm = () => {
    let errors = {};

    if (auth.isLoggedUserAdmin() && !formData.customer_code) {
      errors = { ...errors, customer_code: t("errorMsg.select.customer") };
    }

    if (!isEmail(formData.email ? formData.email : "")) {
      errors = { ...errors, email: t("errorMsg.notValid.emailAddress") };
    }

    if (!blockAllAccounts && getAccountsFormData(formData).length === 0) {
      errors = { ...errors, rules: t("errorMsg.select.deliveryAccount") };
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const submit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setFormLoading();
      await api.addToSuppressionList({
        email: formData.email,
        reason: formData.reason,
        accounts: blockAllAccounts ? [] : getAccountsFormData(formData),
        customer_code: formData.customer_code,
      });
      unsetFormLoading();

      alertSuccess(t("delivery.suppressionList.add.alert.success"));

      history.push("/delivery/suppression-list");
    } catch (error) {
      console.error(error);
      await handleFormResponseErrors(error);
      unsetFormLoading();
    }
  };

  const getAccounts = async (customerCode = null) => {
    try {
      const resp = await api.getDeliveryAccounts({ customer_code: customerCode });
      setAccounts(resp.resources);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!auth.isLoggedUserAdmin()) {
      getAccounts();
    }
  }, [auth]);

  useEffect(() => {
    if (formData.customer_code) {
      getAccounts(formData.customer_code);
    }
  }, [formData.customer_code]);

  return (
    <div className="contentWrapper">
      <div className="screenContainer flexAlignCenter">
        <div className="mt-4 w-full max-w-[--form-page-width]">
          <div className="sectionTitle titleFP">{t("delivery.suppressionList.add.title")}</div>
          <div className="sectionDesc">{t("delivery.suppressionList.add.desc")}</div>

          <Button docsKey="deliveryAddSuppressionList" className="btnDocsFP" color="docs" sizeSmall>
            button.docs
          </Button>
        </div>

        <div className="formContainer">
          <div className="flexColumnContainer">
            <div className="formSection">
              {auth.isLoggedUserAdmin() && (
                <CustomerSelect
                  withoutDomainCounter
                  initialValue=""
                  className="inputFull2"
                  label={t("input.label.customer")}
                  onChange={(data) => {
                    setFormFieldValue({ customer_code: data ? data.value : null });
                    if (!data) {
                      setAccounts([]);
                    }
                  }}
                  errors={formErrors.customer_code}
                />
              )}
            </div>

            <div className="formSection">
              <Input
                className="inputFull2"
                name="email"
                label={t("delivery.suppressionList.add.email.label")}
                placeholder={"email"}
                type="email"
                value={formData.email}
                errors={formErrors.email}
                onChange={setFormFieldValue}
                autoCapitalize="off"
                required
              />
            </div>

            <div className="formSection">
              <RadioButton
                grouplabel={t("delivery.suppressionList.add.reason.title")}
                className={"inputFull2"}
                //eslint-disable-next-line
                children={[
                  {
                    value: "bounce",
                    label: t("delivery.suppressionList.add.reason.bounce.label"),
                  },
                  {
                    value: "complaint",
                    label: t("delivery.suppressionList.add.reason.complain.label"),
                  },
                ]}
                name="reason"
                inRow
                value={formData.reason}
                onChange={setFormFieldValue}
              />
            </div>
            <div className="formSection">
              <RestrictionRules
                accounts={accounts}
                onSelectAllChange={setBlockAllAccounts}
                checkedAccounts={formData.accounts}
                onChange={({ accounts }) => setFormFieldValue({ accounts })}
                allSelected={blockAllAccounts}
              />
              {formErrors.rules && <label className={s.errorText}>{formErrors.rules}</label>}
            </div>
          </div>

          <div className="flexColumnContainer">
            {formErrors.onForm && (
              <div className="formSection">
                <WarningBanner color="red" msg={formErrors.onForm} />
              </div>
            )}
            <div className="formFooter">
              <Button
                color="text"
                type="button"
                onClick={() => {
                  history.push("/delivery/suppression-list");
                }}
              >
                button.cancel
              </Button>
              <Button onClick={submit} color="accent" loading={isFormLoading}>
                button.add
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default New;
